import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button, ButtonGroup, Spinner, Tab, Tabs, useTabParam } from '@structural-climbing/ui'
import { useGymContext } from '../contexts/GymContext'
import { AgendaView, WeekView } from '../features/calendar/CalendarViews'
import { ClassDrawer } from '../features/calendar/ClassDrawer'
import { ClassFormModal } from '../features/calendar/ClassFormModal'
import { MonthView } from '../features/calendar/MonthView'
import { YearView } from '../features/calendar/YearView'
import type { CalendarEvent, ClassSession } from '../features/calendar/types'
import { useClasses, useClassSummary } from '../features/calendar/useClasses'
import {
  addDays,
  addMonths,
  gymToday,
  mondayOf,
  monthGrid,
  monthTitle,
  weekDays,
  weekTitle,
} from '../features/calendar/weekRange'
import { PageHeader } from '../components/PageHeader'

const VIEWS = ['week', 'month', 'year', 'agenda'] as const
type View = (typeof VIEWS)[number]

/** A phone gets the list; seven columns do not fit. */
function defaultView(): View {
  return typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)').matches
    ? 'week'
    : 'agenda'
}

export function CalendarPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { slug, timezone } = useGymContext()
  const today = gymToday(timezone)

  // One day decides what is on screen: its week, its month or its year.
  const [cursor, setCursor] = useState(today)
  const [fallbackView] = useState(defaultView)
  const [view, setView] = useTabParam<View>('view', VIEWS, fallbackView)

  const week = useMemo(() => weekDays(mondayOf(cursor)), [cursor])
  const grid = useMemo(() => monthGrid(cursor), [cursor])
  const year = cursor.slice(0, 4)

  // Only the request the view needs. Week and list share a range; the month
  // reads its six-week grid; the year reads counts only.
  const listRange = view === 'month' ? [grid[0], grid[41]] : [week[0], week[6]]
  const list = useClasses(listRange[0], listRange[1], view !== 'year')
  const summary = useClassSummary(`${year}-01-01`, `${year}-12-31`, view === 'year')
  const { classes, events, reload } = list
  const active = view === 'year' ? summary : list
  const canManage = active.canManage

  const [open, setOpen] = useState<ClassSession | null>(null)
  const [form, setForm] = useState<{ session: ClassSession | null } | null>(null)

  // Keep the open drawer in step with a reload (places taken, an edit).
  useEffect(() => {
    if (!open) return
    const fresh = classes.find((c) => c.id === open.id)
    if (fresh && fresh !== open) setOpen(fresh)
  }, [classes, open])

  function openEvent(event: CalendarEvent) {
    navigate(event.kind === 'competition' ? `/${slug}/competitions/${event.id}` : `/${slug}/news`)
  }

  function step(direction: 1 | -1) {
    setCursor((c) =>
      view === 'year'
        ? addMonths(c, 12 * direction)
        : view === 'month'
          ? addMonths(c, direction)
          : addDays(c, 7 * direction),
    )
  }

  const openDay = (day: string) => {
    setCursor(day)
    setView('week')
  }

  const title =
    view === 'year'
      ? year
      : view === 'month'
        ? monthTitle(cursor, i18n.language)
        : weekTitle(week[0], i18n.language)

  const unit = view === 'year' ? 'Year' : view === 'month' ? 'Month' : 'Week'

  const viewProps = {
    days: week,
    today,
    classes,
    events,
    onOpenClass: setOpen,
    onOpenEvent: openEvent,
  }

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <PageHeader
        title={t('calendar.title')}
        subtitle={canManage ? t('calendar.subtitle') : t('calendar.subtitleTrainer')}
        primaryAction={
          canManage
            ? { label: t('calendar.newClass'), onPress: () => setForm({ session: null }) }
            : undefined
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ButtonGroup size="sm" variant="flat">
            <Button isIconOnly aria-label={t(`calendar.previous${unit}`)} onPress={() => step(-1)}>
              ‹
            </Button>
            <Button onPress={() => setCursor(today)}>{t('calendar.today')}</Button>
            <Button isIconOnly aria-label={t(`calendar.next${unit}`)} onPress={() => step(1)}>
              ›
            </Button>
          </ButtonGroup>
          <h2 className="text-base font-semibold first-letter:uppercase" data-testid="week-title">
            {title}
          </h2>
        </div>
        <Tabs
          size="sm"
          selectedKey={view}
          onSelectionChange={(k) => setView(k as View)}
          aria-label={t('calendar.viewLabel')}
        >
          <Tab key="week" title={t('calendar.viewWeek')} />
          <Tab key="month" title={t('calendar.viewMonth')} />
          <Tab key="year" title={t('calendar.viewYear')} />
          <Tab key="agenda" title={t('calendar.viewAgenda')} />
        </Tabs>
      </div>

      {active.loading ? (
        <div className="flex justify-center py-10">
          <Spinner size="sm" color="primary" />
        </div>
      ) : active.error ? (
        <p className="text-sm text-danger">{t('calendar.errorLoad')}</p>
      ) : view === 'year' ? (
        <YearView
          year={cursor}
          today={today}
          days={summary.days}
          events={summary.events}
          onOpenDay={openDay}
          onOpenMonth={(day) => {
            setCursor(day)
            setView('month')
          }}
        />
      ) : view === 'month' ? (
        <div className="overflow-x-auto">
          <div className="min-w-[48rem]">
            <MonthView
              days={grid}
              month={cursor}
              today={today}
              classes={classes}
              events={events}
              onOpenClass={setOpen}
              onOpenEvent={openEvent}
              onOpenDay={openDay}
            />
          </div>
        </div>
      ) : view === 'week' ? (
        <div className="overflow-x-auto">
          <div className="min-w-[56rem]">
            <WeekView {...viewProps} />
          </div>
        </div>
      ) : (
        <AgendaView {...viewProps} />
      )}

      <ClassDrawer
        session={open}
        canManage={canManage}
        onClose={() => setOpen(null)}
        onEdit={(session) => setForm({ session })}
        onChanged={() => void reload()}
      />

      <ClassFormModal
        isOpen={form !== null}
        session={form?.session ?? null}
        defaultDay={cursor < today ? today : cursor}
        onClose={() => setForm(null)}
        onSaved={() => {
          setOpen(null)
          void reload()
        }}
      />
    </div>
  )
}
